/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { mapModifiers } from 'libs/component';
import { GlobalHeader, GlobalHeaderItem } from 'components/organisms/global-header';
import Link from 'next/link';
import { withRouter, NextRouter } from 'next/router';
import { GlobalFooter, GlobalFooterSocialItem } from 'components/organisms/global-footer';
import { Whole } from 'components/organisms/whole';
import { BackToTopButton } from 'components/atoms/back-to-top-button';
import { SubscribeBox } from 'components/molecules/subscribe-box';
import _ from 'lodash';
import { Waypoint } from 'react-waypoint';
import { PageLoading } from 'components/atoms/page-loading';
import { API_KEY, LANGUAGE_OPTIONS, MAIN_NAV_ITEMS, SOCIAL_ITEM_LIST, TAXIMAIL_API } from 'libs/constants';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Modal } from 'components/organisms/modal';
import { Button } from 'components/atoms/button';

export interface DefaultProps {
  router: NextRouter;
  heroImage?: any;
  heroImageSp?: any;
  children?: React.ReactNode;
  isLoading?: boolean;
  isStartLoading?: boolean;
  loadingProgress?: number;
}

interface NavItemProps {
  label: string;
  link: string;
}

interface SocialItemProps {
  type: string;
  link: string;
}

const navList: NavItemProps[] = MAIN_NAV_ITEMS;

const languageOptions = LANGUAGE_OPTIONS;

const socialList: SocialItemProps[] = SOCIAL_ITEM_LIST;

const Default: React.FC<DefaultProps> = ({
  router,
  children,
  heroImage,
  heroImageSp,
  isLoading = false,
  isStartLoading = false,
  loadingProgress = 0,
}) => {
  const { t, i18n } = useTranslation();
  // const router = useRouter();
  const [heroBackground, setHeroBackground] = useState(heroImage);
  // const [language, setLanguage] = useState('en');
  const [stickBackToTopButton, setStickBackToTopButton] = useState(false);

  const language = useMemo(() => {
    return i18n.language || 'cn';
  }, [i18n.language]);

  const onLanguageChange: any = useCallback(
    (language: string) => {
      // router.push(`/${language}/${router.asPath.substring(4)}`);
      if(language !== "/"){
        router.push(`${language}/${router.asPath.substring(4)}`);
      }
    },
    [router]
  );

  const isActiveNav = (link: string) => {
    const currentPath = router?.asPath;
    if (currentPath) {
      const urlParts = currentPath.replace(`/${language}/`, '/').split('/');
      const linkKey = link.replace(`/${language}/`, '/').substring(1);

      // check root path
      if (urlParts[1] === linkKey) return true;
    }
    return false;
  };

  const getNavLink = (link: string) => {
    if (link === '/') {
      return `/${language}`;
    }
    return `/${language}${link}`;
  };

  const adjustHeroImage = _.debounce(
    useCallback(() => {
      if (heroImage && heroImageSp) {
        const isMobile = window.innerWidth <= 640;
        if (isMobile) {
          setHeroBackground(heroImageSp);
        } else {
          setHeroBackground(heroImage);
        }
      }
    }, [heroImage, heroImageSp]),
    150
  );

  useEffect(() => {
    window.addEventListener('resize', adjustHeroImage);
    adjustHeroImage();

    return () => {
      window.removeEventListener('resize', adjustHeroImage);
    };
  }, [adjustHeroImage]);

  const [isSubscribeLoading, setIsSubscribeLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [apiErrorMessage, setApiErrorMessage] = useState('');

  const handleCloseThankyouModal = useCallback(() => {
    setShowSuccessModal(false);
  }, []);

  const handleCloseErrorModal = useCallback(() => {
    setShowErrorModal(false);
  }, []);

  const handleSubscribe = useCallback(async (submitData: any) => {
    const http = axios.create({
      baseURL: TAXIMAIL_API.BASE_URL,
    });
    try {
      submitData.auth = API_KEY;
      setIsSubscribeLoading(true);
      // login first
      const loginPayload = {
        auth: API_KEY,
        apiKey: TAXIMAIL_API.API_KEY,
        secretKey: TAXIMAIL_API.SECRET_KEY,
      };
      const loginResult = await http.post(TAXIMAIL_API.LOGIN, loginPayload, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
      if (loginResult.data) {
        const sessionId = loginResult.data.data.session_id;
        submitData.sessionId = sessionId;
        // subscribe to mailing list
        const subscribeResult = await http.post(TAXIMAIL_API.IMPORT_CONTACT, submitData, {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        });
        setIsSubscribeLoading(false);
        // show thank you dialog
        if (subscribeResult) {
          // show modal
          setShowSuccessModal(true);
        }
      }
    } catch (e: any) {
      setIsSubscribeLoading(false);
      let errorMessage = e.message;
      if (e.response) {
        if (e.response.data.error) {
          if (e.response.data.error.err_msg) {
            errorMessage = e.response.data.error.err_msg;
          } else {
            errorMessage = e.response.data.error;
          }
        } else {
          errorMessage = e.response.data;
        }
      }
      setApiErrorMessage(errorMessage);
      setShowErrorModal(true);
    }
  }, []);

  return (
    <div className={mapModifiers('t-default', language)}>
      {/* LOADER */}
      {isLoading && <PageLoading animated={isStartLoading} progress={loadingProgress} />}
      {/* HEADER */}
      <GlobalHeader defaultLanguage={language} languageOptions={languageOptions} onLanguageChange={onLanguageChange}>
        {navList.map((navItem, index) => (
          <GlobalHeaderItem
            key={index}
            active={isActiveNav(`${getNavLink(navItem.link)}`)}
            component={
              <Link href={`${getNavLink(navItem.link)}`} locale={false}>
                <a>{navItem.label}</a>
              </Link>
            }
          />
        ))}
      </GlobalHeader>
      {/* /HEADER */}

      {/* CONTENT */}
      <main className="t-default__main">
        <Whole>
          {heroBackground && (
            <div
              className="t-default__hero-banner"
              style={{
                backgroundImage: `url('${heroBackground}')`,
              }}
            ></div>
          )}
          <div className="t-default__content">{children}</div>

          {/* DECORATION */}
          {/* <div className="t-default__deco-1"></div>
          <div className="t-default__deco-2"></div> */}
          {/* /DECORATION */}
        </Whole>
      </main>

      <div className={mapModifiers('t-default__back-to-top', stickBackToTopButton && 'stick')}>
        <BackToTopButton />
      </div>

      <div className="t-default__subscribe-box">
        <Waypoint onEnter={() => setStickBackToTopButton(true)} onLeave={() => setStickBackToTopButton(false)} />
        <SubscribeBox onSubmit={handleSubscribe} isSubmitting={isSubscribeLoading} />
      </div>
      {/* /CONTENT */}

      {/* FOOTER */}
      <GlobalFooter
        socials={socialList.map((socialItem, key) => (
          <GlobalFooterSocialItem key={key} type={socialItem.type as any} link={socialItem.link} />
        ))}
      />
      {/* /FOOTER */}

      {/* Subscribe Modal */}
      <Modal opened={showSuccessModal} noHeader={true} noCloseButton={true} noBackground={true}>
        <div className="p-thankyou-modal">
          <div className="p-thankyou-modal__overlay" onClick={handleCloseThankyouModal}></div>
          <div className="p-thankyou-modal__content">
            <div className="p-thankyou-modal__content-icon">
              <img src="/images/thankyou.svg" alt="thank you" width="140" height="140" />
            </div>
            <p className="p-thankyou-modal__content-title">{t('contact.thankyou.title')}</p>
            <p
              className="p-thankyou-modal__content-description"
              dangerouslySetInnerHTML={{ __html: t('contact.thankyou.description') }}
            />
            <div className="p-thankyou-modal__content-button">
              <Button onClick={handleCloseThankyouModal}>{t('general.close')}</Button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal opened={showErrorModal} noHeader={true} noCloseButton={true} noBackground={true}>
        <div className="p-thankyou-modal">
          <div className="p-thankyou-modal__overlay" onClick={handleCloseErrorModal}></div>
          <div className="p-thankyou-modal__content">
            <p className="p-thankyou-modal__content-title">{t('contact.error')}</p>
            <p className="p-thankyou-modal__content-description">
              <code>{apiErrorMessage}</code>
            </p>
            <div className="p-thankyou-modal__content-button">
              <Button onClick={handleCloseErrorModal}>{t('general.close')}</Button>
            </div>
          </div>
        </div>
      </Modal>
      {/* /Subscribe Modal */}
    </div>
  );
};

export default withRouter(Default);
